/**
 * @packageDocumentation
 * @module f1/boolean
 */

/** @ignore */
type t_condition_fn<VALUE> = (value: VALUE) => boolean;
/** @ignore */
type t_action_fn<VALUE, RETURN> = (value: VALUE) => RETURN;

export interface Either {
  /**
   * Invoke a function which calls `then` if the `predicate` is true and `otherwise` if else
   *
   * @public
   * @function f1.either
   * @param then the action to invoke if the predicate is `true` (ie. `then`)
   * @param otherwise the action to invoke if the predicate is `false` (ie. `else`)
   * @param predicate the evaluation of a value to decide if the `then` should be invoked, return `true`
   * @param value the value to evaluate and act upon
   * @example
   *
   * ```ts
   * f1.either((value) => value - 1, (value) => value + 1, (value) => value > 5, 7) === 6
   * f1.either((value) => value - 1, (value) => value + 1, (value) => value < 5, 7) === 7
   * ```
   */
  <
    ELSE extends t_action_fn<VALUE, RETURN>,
    THEN extends t_action_fn<VALUE, RETURN>,
    PREDICATE extends t_condition_fn<VALUE>,
    VALUE,
    RETURN
  >(
    otherwise: ELSE,
    then: THEN,
    predicate: PREDICATE,
    value: VALUE
  ): RETURN;

  <
    ELSE extends t_action_fn<VALUE, RETURN>,
    THEN extends t_action_fn<VALUE, RETURN>,
    PREDICATE extends t_condition_fn<VALUE>,
    VALUE,
    RETURN
  >(
    otherwise: ELSE,
    then: THEN,
    predicate: PREDICATE
  ): (value: VALUE) => RETURN;
}

/**
 * @see Either
 */
export default <
  ELSE extends t_action_fn<VALUE, RETURN>,
  THEN extends t_action_fn<VALUE, RETURN>,
  PREDICATE extends t_condition_fn<VALUE>,
  VALUE,
  RETURN
>(
  otherwise: ELSE,
  then: THEN,
  predicate: PREDICATE,
  value: VALUE
) => (predicate(value) === true ? then(value) : otherwise(value));
